import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Eye, EyeOff, Plus, RefreshCcw } from "lucide-react";

import Header from "../../../Component/Common/Header";
import BlueButton from "../../../Component/Common/BlueButton";

import Badge from "../../../Component/Common/Badge";
import { useNavigate } from "react-router-dom";
import WarrantyLoader from "../../../Component/Loader/RegularLoader";
import SellLoader from "../../../Component/Loader/SellLoader";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import SectionCard from "../../../Component/RetUserComponents/SectionCard";
import SimpleTable from "../../../Component/Common/SimpleTable";
import RefreshButton from "../../../Component/Common/RefreshButton";

export default function Agents() {
  const navigate = useNavigate();
  const [allAgents, setAllAgents] = useState([]);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [activeStates, setActiveStates] = useState({});

  const authUserString = localStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);
  const loginUserID = authUser?.id;

  const fetchAgents = async () => {
    if (!loginUserID) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `https://goelectronix.in/api/v1/get-allagent2?page=${page}&per_page=10&distributortID=${loginUserID}`
      );
      const dataPage = res.data.data || [];
      setList(dataPage.data || []);
      setLastPage(dataPage.last_page || 1);

      const initial = {};
      (dataPage.data || []).forEach((agent) => {
        initial[agent.ID] = agent.IS_Active === 1;
      });
      setActiveStates(initial);
    } catch (err) {
      console.error("Error fetching agents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [loginUserID, page]);

  useEffect(() => {
    const fetchAll = async () => {
      if (!loginUserID) return;
      try {
        let all = [];
        let pageNo = 1;
        let last = 1;
        while (pageNo <= last) {
          const res = await axios.get(
            `https://goelectronix.in/api/v1/get-allagent2?page=${pageNo}&per_page=10&distributortID=${loginUserID}`
          );
          const pageData = res.data.data;
          if (!pageData) break;
          all = [...all, ...(pageData.data || [])];
          last = pageData.last_page || 1;
          pageNo++;
        }
        setAllAgents(all);
        const initial = {};
        all.forEach((agent) => {
          initial[agent.ID] = agent.IS_Active === 1;
        });
        setActiveStates(initial);
      } catch (err) {
        console.error("Error fetching all agents:", err);
      }
    };
    fetchAll();
  }, [loginUserID]);

  const filtered = useMemo(() => {
    if (!filter) return list;
    const term = filter.toLowerCase();
    return allAgents.filter(
      (r) =>
        (r.agentName && r.agentName.toLowerCase().includes(term)) ||
        (r.agentCode && r.agentCode.toLowerCase().includes(term))
    );
  }, [filter, list, allAgents]);

  const handleToggleActiveConfirmation = (agent) => {
    const { ID, agentName } = agent;
    const newActive = !activeStates[ID];
    const action = newActive ? "activate" : "deactivate";
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${action} ${agentName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${action} it!`,
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "rounded-2xl shadow-lg p-6 bg-white",
        title: "text-lg font-semibold text-gray-900",
        content: "text-sm text-gray-600",
        confirmButton:
          "bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg",
        cancelButton:
          "bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2 rounded-lg",
      },
    }).then((res) => {
      if (res.isConfirmed) {
        handleToggleActive(agent, newActive);
      }
    });
  };

  const handleToggleActive = async (agent, newActive) => {
    const { ID, agentName } = agent;
    setActiveStates((prev) => ({
      ...prev,
      [ID]: newActive,
    }));
    try {
      const apiUrl = `https://goelectronix.in/api/v1/update-agent/${ID}`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ IS_Active: newActive ? 1 : 0 }),
      });
      if (!response.ok) {
        setActiveStates((prev) => ({
          ...prev,
          [ID]: !newActive,
        }));
        const errorData = await response.json();
        Swal.fire("Error", `Failed to update status: ${errorData?.message}`, "error");
        return;
      }
      Swal.fire(
        newActive ? "Activated" : "Deactivated",
        `Agent ${agentName} status changed.`,
        "success"
      );
    } catch (err) {
      setActiveStates((prev) => ({
        ...prev,
        [ID]: !newActive,
      }));
      Swal.fire("Error", `Failed to update status: ${err.message}`, "error");
    }
  };

  return (
    <div>
      <SectionCard>
        <div className="space-y-3 sm:space-y-4">
      <Header>
        <div className="flex flex-row sm:flex-row sm:items-center sm:gap-2 gap-2 w-full">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by agent Code or Name"
            className="w-full sm:w-1/2 rounded-lg border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <BlueButton
              onClick={() => navigate("/add-agent")}
              className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: "#0B5ED7" }}
            >
              <Plus className="mr-1" size={14} /> Add
            </BlueButton>
            <RefreshButton onClick={fetchAgents} />
          </div>
        </div>
      </Header>

      {loading ? (
        <div className="flex items-center justify-center w-full translate-y-10">
          <RegularLoader />
        </div>
      ) : (
        <>
          <SimpleTable
            headers={[
              "Sr No.",
              "Created At",
              "Agent Name (Code)",
              "Mobile No",
              "Address",
              "District",
              "Status",
              "Consent",
              "Bank Verified",
              "GST Details",
              "Actions",
            ]}
            rows={filtered.map((r, index) => [
              <span key={`sr-${r.ID}`}>{index + 1}</span>,
              <div key={r.ID} className="font-medium">
                {r.agentName}{" "}
                <span className="text-xs text-slate-500">({r.agentCode})</span>
              </div>,
              <span key={`d-${r.ID}`}>{r.District || "-"}</span>,
              <span key={`m-${r.ID}`}>{r.MobileNo || "-"}</span>,
              <Badge
                key={`b-${r.ID}`}
                label={activeStates[r.ID] ? "Active" : "Inactive"}
                color={activeStates[r.ID] ? "blue" : "red"}
              />,
              <span key={`c-${r.ID}`}>{new Date(r.CreatedOn).toLocaleDateString()}</span>,
              <div className="flex flex-wrap gap-2" key={`act-${r.ID}`}>
                <BlueButton onClick={() => handleToggleActiveConfirmation(r)}>
                  {activeStates[r.ID] ? "Deactivate" : "Activate"}
                </BlueButton>
              </div>,
            ])}

            currentPage={page}
            totalPages={lastPage}
            onPageChange={setPage}
            filter={filter}
          />

        </>
      )}
    </div>
      </SectionCard>
    </div>
  );
}
